'use client'

import Image from 'next/image'
import React from 'react'
import useSWRImmutable from 'swr/immutable'

import ArticleContentIterator from '@/legacy-src/components/counselling/ArticleContentIterator'
import ArticleHeader from '@/legacy-src/components/counselling/ArticleHeader'
import ArticleHeaderImg from '@/legacy-src/components/counselling/ArticleHeaderImg'
import { ArticleType } from '@/legacy-src/components/hedepyPlus/types'
import { useSession } from '@/legacy-src/contexts/session-context'
import { fetchAllImageDimensions } from '@/legacy-src/lib/strapi/fetchFunctions'

import useFlags from '../../hooks/useFlags'
import { getTranslate } from '../../utilities/i18n'
import BackButton from '../button/BackButton'
import ArticleAnchors from './ArticleAnchors'
import CounsellingTeaser from './CounsellingTeaser'
import RecommendeContent from './RecommendedContent'
import RecommendedVideos from './RecommendedVideos'
import { chooseCorrectColors } from './utils/chooseCorrectColors'
import { ARTICLE_CATEGORIES } from './utils/constants'

type ImgProps = {
  alt: string
  title?: string
  src: string
  className?: string
}

const useImageDimensions = (url: string) => {
  const { data } = useSWRImmutable('useImageDimensions', fetchAllImageDimensions)
  return data ? data[url] : null
}

export const OptimizedImage: React.FC<ImgProps> = (props) => {
  const { t } = getTranslate()
  const isSvg = props.src.includes('.svg')
  const alt = props.alt ?? t('general.illustrationImage')
  const imgDimensions = useImageDimensions(props.src)

  if (isSvg) {
    const src = chooseCorrectColors(props.src)
    return <img {...props} src={src} loading="lazy" alt={alt} />
  }

  const imgProps = { ...props, alt: alt, sizes: '720px' }

  return imgDimensions ? (
    <Image {...imgProps} width={imgDimensions.w} height={imgDimensions.h} alt={alt} />
  ) : (
    <span className="relative flex h-[300px] big-phone:h-[400px]">
      <Image {...imgProps} fill className="max-w-max object-cover tablet:object-contain" alt={alt} />
    </span>
  )
}

type Props = {
  readonly data: any // @todo types
  readonly videoLessons: any
  readonly topics?: any
  readonly isPublic?: boolean
  readonly isMainContent?: boolean
  readonly diagnosisTeaser: React.ReactNode
  readonly baseLink?: string
}

const ArticleDetail: React.FC<Props> = ({
  data,
  videoLessons,
  topics,
  isPublic,
  isMainContent,
  diagnosisTeaser,
  baseLink,
}) => {
  const { t } = getTranslate()
  const { showCounsellingTeaser } = useFlags()
  const { isLoggedIn } = useSession()

  if (!data) {
    return <p>There is no content for this version of language.</p>
  }

  const renderRecommendedContent = () =>
    isMainContent ? <RecommendeContent topics={topics} /> : <RecommendedVideos videoLessons={videoLessons} />

  const renderAuthenticatedTeasers = () => (
    <div className="mb-13">
      <h2 className="mb-4 mt-12 w-full text-secondary">
        {t(
          `component.counselling.article.${
            data.category === ARTICLE_CATEGORIES.aboutTherapy ? 'subTitleTherapy' : 'subTitle'
          }`,
        )}
      </h2>

      <div className="mb-12 grid w-full grid-cols-1 gap-6 desktop:grid-cols-2">
        {diagnosisTeaser}
        {showCounsellingTeaser && <CounsellingTeaser />}
      </div>

      <BackButton icon="back" link={baseLink} text={t(`component.button.back`)} size="sm" />
    </div>
  )

  return (
    <>
      <div className="mb-8 grid grid-cols-3 gap-12">
        <div className="col-span-3 tablet:col-span-2">
          <ArticleHeaderImg content={data} />
        </div>
        <div className="col-span-3 tablet:col-span-2">
          <ArticleHeader author={data.author} title={data.title} isPublic />
          <p className="mt-8">{data.perex}</p>
          <ArticleContentIterator content={data.content} isPublic={isPublic} rewriteLinksToPublic={!isLoggedIn} />
          <div className="mt-8 tablet:hidden">{renderRecommendedContent()}</div>
          {!isPublic && data.type !== ArticleType.WEBINAR && renderAuthenticatedTeasers()}
        </div>
        <div className="col-span-1 hidden tablet:block">
          <ArticleAnchors content={data.content} />
          {renderRecommendedContent()}
        </div>
      </div>
    </>
  )
}

export default ArticleDetail
